import { Link, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatUSD, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, ClipboardList, Banknote, CalendarDays, ChevronRight } from "lucide-react";

export default function MobileHome() {
  const { activeOrgId } = useAuth();
  const location = useLocation();
  const [cash, setCash] = useState({ total: 0, items: [] });
  const [counts, setCounts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!activeOrgId) return;
    try {
      const [c, mine, svc] = await Promise.all([
        api.get(`/organizations/${activeOrgId}/cash-on-hand`),
        api.get(`/organizations/${activeOrgId}/counts`),
        api.get(`/organizations/${activeOrgId}/services`),
      ]);
      setCash(c.data);
      setCounts(mine.data.slice(0, 5));
      setServices(svc.data.slice(0, 4));
    } finally { setLoading(false); }
  }, [activeOrgId]);

  // Refresh on mount, route entry, and tab refocus so deletes elsewhere reflect immediately.
  useEffect(() => { refresh(); }, [refresh, location.key]);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refresh]);

  const statusBadge = (s) => {
    if (s === "verified") return <Badge className="bg-primary/10 text-primary border border-primary/20 rounded-lg text-[10px]">Verified · Cash on hand</Badge>;
    if (s === "deposited") return <Badge variant="outline" className="rounded-lg text-[10px]">Deposited</Badge>;
    return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 rounded-lg text-[10px]">Pending signatures</Badge>;
  };

  return (
    <div className="space-y-6 fade-up">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Counter Portal</div>
        <h1 className="font-serif text-3xl tracking-tight mt-1">Good to see you</h1>
        <p className="text-sm text-muted-foreground mt-1">Record offerings, track services, post deposits — right from your phone.</p>
      </div>

      <Card className="rounded-2xl border-border shadow-none p-5 bg-primary/5">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Cash on hand</div>
        <div className="font-mono text-3xl mt-1 text-primary tabular-nums" data-testid="m-cash-on-hand-total">
          {formatUSD(cash.total)}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {cash.items.length} verified count{cash.items.length === 1 ? "" : "s"} waiting to be deposited
        </div>
        {cash.items.length > 0 && (
          <Link to="/m/cash" className="mt-3 inline-flex items-center text-sm text-primary font-medium hover:underline" data-testid="m-go-deposit">
            Go to deposits <ChevronRight className="w-4 h-4 ml-0.5" />
          </Link>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Link to="/m/count" data-testid="m-quick-new-count">
          <Card className="rounded-2xl border-border shadow-none p-4 h-full hover:bg-secondary/40 transition-colors">
            <ClipboardList className="w-5 h-5 text-primary" />
            <div className="font-serif text-lg mt-2">New count</div>
            <div className="text-xs text-muted-foreground mt-0.5">Record an offering</div>
          </Card>
        </Link>
        <Link to="/m/services" data-testid="m-quick-new-service">
          <Card className="rounded-2xl border-border shadow-none p-4 h-full hover:bg-secondary/40 transition-colors">
            <CalendarDays className="w-5 h-5 text-primary" />
            <div className="font-serif text-lg mt-2">Service log</div>
            <div className="text-xs text-muted-foreground mt-0.5">Attendance & visitors</div>
          </Card>
        </Link>
      </div>

      <Card className="rounded-2xl border-border shadow-none overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="text-sm font-semibold">Recent counts</div>
          <Link to="/m/count" className="text-xs text-primary"><Plus className="w-3.5 h-3.5 inline mr-0.5" />New</Link>
        </div>
        {loading ? (
          <div className="p-5 text-muted-foreground text-sm">Loading…</div>
        ) : counts.length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">No counts yet. Tap "New count" to start.</div>
        ) : (
          <div className="divide-y divide-border">
            {counts.map((c) => (
              <Link to="/m/cash" key={c.id} className="block px-5 py-3 hover:bg-secondary/40" data-testid={`m-count-row-${c.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.counter_1_name} & {c.counter_2_name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{formatDate(c.date)}</div>
                    <div className="mt-1">{statusBadge(c.status)}</div>
                  </div>
                  <div className="font-mono text-lg tabular-nums">{formatUSD(c.total_amount)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      <Card className="rounded-2xl border-border shadow-none overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="text-sm font-semibold flex items-center gap-2"><Banknote className="w-4 h-4 text-muted-foreground" /> Latest services</div>
          <Link to="/m/services" className="text-xs text-primary">View all</Link>
        </div>
        {services.length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">No service logs yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {services.map((s) => (
              <div key={s.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{s.type_label}{s.name ? ` · ${s.name}` : ""}</div>
                  <div className="text-xs text-muted-foreground">{formatDate(s.date)} · {s.attendance} attended</div>
                </div>
                <div className="font-mono text-sm tabular-nums text-primary">{formatUSD(s.offering_total)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
